import type { ReactNode } from 'react'
import { createContext, useCallback, useContext as useContextCore, useEffect, useMemo } from 'react'
import { useLocalStorage, useMedia } from 'react-use'
import { toggleAttribute, hideBackdrop } from '../utils/layout'

export enum ThemeMode {
  Light = 'light',
  Dark = 'dark',
}

export enum SidenavSize {
  Default = 'default',
  Compact = 'compact',
  Condensed = 'condensed',
  Offcanvas = 'offcanvas',
}

export enum MenuColor {
  Dark = 'dark',
}

export enum TopbarColor {
  Light = 'light',
  Dark = 'dark',
}

type LayoutState = {
  theme: ThemeMode
  sidenavSize: SidenavSize
  menuColor: MenuColor
  topbarColor: TopbarColor
}

type LayoutContextType = {
  state: LayoutState
  updateSettings: (newSettings: Partial<LayoutState>) => void
  toggleTheme: () => void
}

const INIT_STATE: LayoutState = {
  theme: ThemeMode.Light,
  sidenavSize: SidenavSize.Default,
  menuColor: MenuColor.Dark,
  topbarColor: TopbarColor.Light,
}

const STORAGE_KEY = '__hauler-theme-config__'

const LayoutContext = createContext<LayoutContextType>({} as LayoutContextType)

export const LayoutProvider = ({ children }: { children: ReactNode }) => {
  const [settings, setSettings] = useLocalStorage<LayoutState>(STORAGE_KEY, INIT_STATE)
  const state = settings ?? INIT_STATE

  const isMobile = useMedia('(max-width: 991.98px)')
  const isTablet = useMedia('(max-width: 1199.98px)')

  const updateSettings = useCallback(
    (newSettings: Partial<LayoutState>) => {
      setSettings({ ...state, ...newSettings })
    },
    [state, setSettings]
  )

  const toggleTheme = useCallback(() => {
    const newTheme = state.theme === ThemeMode.Light ? ThemeMode.Dark : ThemeMode.Light
    setSettings({ ...state, theme: newTheme })
  }, [state, setSettings])

  // Sync data attributes on <html> when settings change
  useEffect(() => {
    hideBackdrop()
    toggleAttribute('data-bs-theme', state.theme)
    toggleAttribute('data-sidenav-size', state.sidenavSize)
    toggleAttribute('data-menu-color', state.menuColor)
    toggleAttribute('data-topbar-color', state.topbarColor)
  }, [state.theme, state.sidenavSize, state.menuColor, state.topbarColor])

  // Auto-switch sidenavSize based on viewport width
  useEffect(() => {
    const size = isMobile ? SidenavSize.Offcanvas : isTablet ? SidenavSize.Condensed : SidenavSize.Default
    if (state.sidenavSize !== size) {
      setSettings({ ...state, sidenavSize: size })
    }
  }, [isMobile, isTablet, state, setSettings])

  const value = useMemo<LayoutContextType>(
    () => ({ state, updateSettings, toggleTheme }),
    [state, updateSettings, toggleTheme]
  )

  return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>
}

export const useLayoutContext = () => useContextCore(LayoutContext)
